// kfe-now-playing.js — persistent "now playing" chrome indicator (Brief 091 §5,
// design-doc §10).
//
// A slim bar that lives in the app shell as a sibling of <main> (like the
// Brief 089 tab bar), so it PERSISTS across route changes. Shown whenever the
// persistent Global-Playlist player (services.player) has an active track —
// anywhere in the app — and hidden otherwise. It surfaces the current track
// title + a play/pause toggle, and tapping the bar returns to /playlist for
// one-tap return to the playing track.
//
// It is CHROME: it subscribes to the player service's events and updates in
// place; it never re-renders the shell or touches the routed <main> outlet.
//
// The player reference defaults to services.player but can be injected via the
// `.player` property (test seam) — captured before connect by KFEComponent's
// upgrade-safe lazy-property replay.

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t } from "../core/i18n.js";
import { services } from "../platform-services.js";
import * as router from "../router.js";

export class KFENowPlaying extends KFEComponent {
  static props = ["player"];

  static styles = `
    :host {
      display: flex; align-items: center; gap: 0.5rem;
      padding: 0.4rem 0.75rem;
      background: var(--kfe-bg-header);
      border-top: 1px solid var(--kfe-border);
      color: var(--kfe-fg);
    }
    /* Collapse entirely when nothing is playing. Overrides the :host display
       above (the UA [hidden] rule alone can't win against :host{display:flex}). */
    :host([hidden]) { display: none; }

    button {
      background: transparent; border: 0; color: inherit;
      cursor: pointer; -webkit-tap-highlight-color: transparent;
    }
    .open {
      flex: 1; min-width: 0; display: flex; align-items: center; gap: 0.5rem;
      text-align: left; padding: 0.1rem 0;
    }
    .eq {
      flex: none; color: var(--kfe-accent); font-size: 1rem; line-height: 1;
    }
    .meta { min-width: 0; display: flex; flex-direction: column; line-height: 1.2; }
    .label { font-size: 0.65rem; color: var(--kfe-muted); }
    .title {
      font-size: 0.85rem; font-weight: 600;
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
    .toggle { flex: none; font-size: 1.4rem; line-height: 1; padding: 0 0.25rem; }
  `;

  onConnected() {
    const player = /** @type {import("../core/player-service.js").PlayerService} */ (
      /** @type {any} */ (this).player || services.player
    );
    this._player = player;

    const sync = () => this._scheduleRender();
    for (const ev of ["trackchange", "playstate", "queuechange", "timeupdate"]) {
      player.addEventListener(ev, sync);
      this.addCleanup(() => player.removeEventListener(ev, sync));
    }

    // Re-localise on language change.
    const onI18n = () => this._scheduleRender();
    document.addEventListener("kfe:i18n", onI18n);
    this.addCleanup(() => document.removeEventListener("kfe:i18n", onI18n));

    // The first render ran in connectedCallback BEFORE _player was resolved
    // (it fell back to services.player); re-render now so the resolved player's
    // current state is reflected immediately on mount.
    this._scheduleRender();
  }

  _onOpen = () => router.navigate("/playlist");
  _onToggle = (/** @type {Event} */ e) => {
    e.stopPropagation();
    this._player?.togglePlay();
  };

  render() {
    const player = this._player || services.player;
    const active = !!player?.isActive;
    // Toggle visibility in place — chrome collapses when idle.
    this.hidden = !active;
    if (!active) return html``;

    const track = player.currentTrack;
    const playing = player.isPlaying;
    return html`
      <button
        class="open"
        type="button"
        aria-label="${t("nowPlaying.open")}"
        on:click=${this._onOpen}
      >
        <span class="eq" aria-hidden="true">♫</span>
        <span class="meta">
          <span class="label">${t("nowPlaying.label")}</span>
          <span class="title">${track?.title || ""}</span>
        </span>
      </button>
      <button
        class="toggle"
        type="button"
        aria-label="${playing ? t("nowPlaying.pause") : t("nowPlaying.play")}"
        on:click=${this._onToggle}
      >${playing ? "⏸" : "▶"}</button>
    `;
  }
}
